/**
 * Friends watching (D-120): Join, Watch and Save me a seat on a friend who is at a table, and the copy they share.
 * Each call resolves to where to go next or throws the server's sentence (ranked refused, watching off, removed…).
 */
import type { FriendActivity, RoomView } from "@digu/shared";
import { api } from "./api";

/** Takes the friend's open seat; resolves to the waiting room (or the table, if it dealt meanwhile). */
export async function joinFriendTable(code: string): Promise<string> {
  const room = await api.post<RoomView>("/rooms/join", { code });
  return room.status === "lobby" ? `/lobby/${encodeURIComponent(code)}` : `/table/${encodeURIComponent(code)}`;
}
/** Starts watching; the table route shows the watching screen for a spectator view. */
export async function watchTable(code: string): Promise<string> {
  await api.post<RoomView>(`/rooms/${encodeURIComponent(code)}/watch`, {});
  return `/table/${encodeURIComponent(code)}`;
}
export async function stopWatching(code: string): Promise<void> {
  try { await api.post(`/rooms/${encodeURIComponent(code)}/watch/leave`, {}); } catch { /* leaving goes ahead either way */ }
}
export interface SeatSaved { ok: true; saved: boolean; place: number | null }
export async function saveSeat(code: string, cancel = false): Promise<SeatSaved> {
  return api.post<SeatSaved>(`/rooms/${encodeURIComponent(code)}/save-seat`, cancel ? { cancel: true } : {});
}

const ORD = ["th", "st", "nd", "rd"];
export function ordinal(n: number): string {
  const v = n % 100;
  return `${n}${v >= 11 && v <= 13 ? "th" : ORD[n % 10] ?? "th"}`;
}
/** "Seat saved · 2nd in line", or "Seat saved · next in line". */
export function savedLine(place: number | null | undefined): string {
  if (!place) return "Seat saved";
  return place === 1 ? "Seat saved · next in line" : `Seat saved · ${ordinal(place)} in line`;
}
/** The button an activity offers. `canSaveSeat` beside Watch is a secondary link, not a second button. */
export function activityButton(a: FriendActivity): { label: string; kind: "join" | "watch" | "saveSeat" } | null {
  if (!a.action || !a.code) return null;
  return { join: { label: "Join", kind: "join" as const }, watch: { label: "Watch", kind: "watch" as const }, saveSeat: { label: "Save me a seat", kind: "saveSeat" as const } }[a.action];
}
/** "2 watching" under a table's results; nothing when nobody is. */
export const watchingLine = (count: number): string => `${count} watching`;
